import type { ReactNode } from 'react';
import React, { useContext, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useIsNonVisitorUser } from '../../account/useIsLoggedIn';
import Avatar from '../../components/Avatar';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import {
  PostPolicyAction,
  usePostPolicyCheck,
} from '../../post/postPolicyCheck';
import { useAllUsersMap } from '../../user/useAllUsers';
import userPath from '../../utils/url/userPath';
import useIsPublicView from '../../utils/useIsPublicView';
import MentionBase from './MentionBase';

const POST_FRAGMENT = gql(`
  fragment UserMentionProvider_post on PostFields {
    id
    ...usePostPolicyCheck_post
  }
`);

const UserMentionContext = React.createContext<DocumentType<
  typeof POST_FRAGMENT
> | null>(null);

interface UserMentionProviderProps {
  children: ReactNode;
  post: DocumentType<typeof POST_FRAGMENT>;
}

export const UserMentionProvider = ({
  children,
  post,
}: UserMentionProviderProps) => (
  <UserMentionContext.Provider value={post}>
    {children}
  </UserMentionContext.Provider>
);

const PUBLIC_USER_QUERY = gql(/** @complexity 1 */ `
  query PublicUserMention($id: ID!) {
    user: publicUser(id: $id) {
      id
      name
      ...Avatar_user
    }
  }
`);

interface UserMentionProps {
  id: string;
  content: string | null;
  mentionText?: string | null;
  updateCache?: (name?: string) => void;
  link: boolean;
  newTab?: boolean;
  selected?: boolean;
}

const UserMention = ({
  id,
  content,
  mentionText,
  updateCache,
  link,
  newTab,
  selected,
}: UserMentionProps) => {
  const isNonVisitorUser = useIsNonVisitorUser();
  const isPublicView = useIsPublicView();
  const postPolicyCheck = usePostPolicyCheck();

  const usersMap = useAllUsersMap({ includeDeactivated: true });
  const { data: { user: publicUser } = {} } = useQuery(PUBLIC_USER_QUERY, {
    variables: { id },
    skip: !isPublicView,
  });

  const user = usersMap[id] ?? publicUser;

  useEffect(() => {
    if (!isPublicView && user?.name != null && content == null) {
      updateCache?.(user.name);
    }
  }, [content, updateCache, isPublicView, user?.name]);

  const isDeactivated =
    user && 'deactivatedAt' in user && user.deactivatedAt != null;

  const post = useContext(UserMentionContext);

  const canViewPost =
    // `usePostPolicyCheck` relies on `useAllUsersMap`, which is not available in public posts and sometimes not in embeds.
    // Therefore, if we don't have a post, we assume the user can view the post
    !user ||
    post?.__typename !== 'Post' ||
    postPolicyCheck(PostPolicyAction.ViewPost, post, user);

  const hasNoAccess = isDeactivated || !canViewPost;

  const avatarUser = user ?? (content ? { id, name: content } : null);

  return (
    <MentionBase
      icon={avatarUser && <Avatar user={avatarUser} size="small" />}
      type="user"
      content={mentionText || (user?.name ?? content ?? undefined)}
      muted={hasNoAccess}
      to={link && user && isNonVisitorUser ? userPath(user) : undefined}
      newTab={newTab}
      selected={selected}
    />
  );
};

export default UserMention;
